import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import { CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DOMPurify from "dompurify";
import { fontFamily } from "../Constants/constants";

function ExcelViewer({ fileBytes }) {
  const [workbook, setWorkbook] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    try {
      const parsedWorkbook = XLSX.read(fileBytes, { type: "array", cellDates: true });
      setWorkbook(parsedWorkbook);
      setSelectedSheet(parsedWorkbook.SheetNames[0] || "");
      setLoading(false);
    } catch {
      setError("Failed to parse Excel file");
      setLoading(false);
    }
  }, [fileBytes]);

  if (loading)
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
        <CircularProgress />
      </div>
    );
  if (error) return <div style={{ padding: "20px", color: "red", fontFamily }}>{error}</div>;

  if (!workbook || !selectedSheet) {
    return <div style={{ padding: "20px", fontFamily }}>This workbook has no visible sheets.</div>;
  }

  const worksheet = workbook.Sheets[selectedSheet];
  const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });
  const columnCount = Math.max(0, ...data.map((row) => row.length));
  const headerRow = data[0] || [];
  const columns = Array.from({ length: columnCount }, (_, index) => ({
    field: `column_${index}`,
    headerName: String(headerRow[index] || XLSX.utils.encode_col(index)),
    minWidth: 140,
    flex: 1,
  }));
  const rows = data.slice(1).map((row, rowIndex) => {
    const gridRow = { id: rowIndex + 1 };
    columns.forEach((column, columnIndex) => {
      gridRow[column.field] = row[columnIndex] ?? "";
    });
    return gridRow;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 420 }}>
      <div
        role="tablist"
        aria-label="Excel sheets"
        style={{
          display: "flex",
          gap: "4px",
          overflowX: "auto",
          padding: "8px 10px 0",
          borderBottom: "1px solid #d8dee8",
          background: "#f7f9fc",
        }}
      >
        {workbook.SheetNames.map((sheetName) => (
          <button
            key={sheetName}
            type="button"
            role="tab"
            aria-selected={selectedSheet === sheetName}
            onClick={() => setSelectedSheet(sheetName)}
            style={{
              border: "1px solid #d8dee8",
              borderBottom: selectedSheet === sheetName ? "2px solid #1976d2" : "none",
              background: selectedSheet === sheetName ? "#fff" : "#edf1f7",
              color: "#1f2937",
              padding: "7px 14px",
              fontFamily,
              fontSize: "12px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {sheetName}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, padding: "10px" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          pageSizeOptions={[]}
          sx={{
            height: "100%",
            fontFamily,
            fontSize: 12,
            backgroundColor: "#fff",
            "& .MuiDataGrid-columnHeaders": { backgroundColor: "#eef3f8" },
            "& .MuiDataGrid-cell": { borderColor: "#e1e6ed" },
          }}
        />
      </div>
    </div>
  );
}

function WordViewer({ fileBytes }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    mammoth
      .convertToHtml({ arrayBuffer: fileBytes })
      .then((result) => {
        setHtml(DOMPurify.sanitize(result.value));
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to parse Word document");
        setLoading(false);
      });
  }, [fileBytes]);

  if (loading)
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
        <CircularProgress />
      </div>
    );
  if (error) return <div style={{ padding: "20px", color: "red", fontFamily }}>{error}</div>;

  return (
    <div
      style={{
        height: "100%",
        minHeight: 420,
        padding: "28px clamp(20px, 5vw, 72px)",
        overflow: "auto",
        fontFamily,
        lineHeight: 1.6,
        color: "#202124",
        background: "#f4f6f8",
      }}
    >
      <style>{`
        .word-document-preview table {
          width: 100%;
          max-width: 100%;
          margin: 1.25rem 0;
          border-collapse: collapse;
          border-spacing: 0;
          table-layout: auto;
          overflow-wrap: anywhere;
        }
        .word-document-preview th,
        .word-document-preview td {
          border: 1px solid #4b5563;
          padding: 6px 8px;
          vertical-align: top;
          text-align: left;
          line-height: 1.35;
        }
        .word-document-preview th {
          background: #f1f3f5;
          font-weight: 700;
        }
        .word-document-preview p {
          margin: 0.45rem 0;
        }
      `}</style>
      <article
        className="word-document-preview"
        style={{
          maxWidth: 900,
          minHeight: "100%",
          margin: "0 auto",
          padding: "clamp(24px, 5vw, 64px)",
          background: "#fff",
          boxShadow: "0 2px 12px rgba(31, 41, 55, 0.08)",
          overflowWrap: "anywhere",
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

// Main Universal Document Viewer Component
function UniversalDocumentViewer({ fileBytes, mimeType, fileName, fileUrl, style }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);

  // Hook for creating and cleaning up blob URLs - moved to top to comply with Rules of Hooks
  useEffect(() => {
    if (fileBytes) {
      if (mimeType === "application/pdf" || fileName?.toLowerCase().endsWith(".pdf")) {
        console.log("📄 Creating PDF blob from file bytes...");
        const blob = new Blob([fileBytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        console.log("✅ PDF blob URL created:", url);
        setBlobUrl(url);

        return () => {
          URL.revokeObjectURL(url);
          setBlobUrl(null);
        };
      } else if (mimeType?.startsWith("image/")) {
        console.log("🖼️ Creating image blob from file bytes...");
        const blob = new Blob([fileBytes], { type: mimeType });
        const url = URL.createObjectURL(blob);
        console.log("✅ Image blob URL created:", url);
        setBlobUrl(url);

        return () => {
          URL.revokeObjectURL(url);
          setBlobUrl(null);
        };
      }
    } else {
      console.log("❌ No file bytes provided to UniversalDocumentViewer");
    }
    return () => {}; // Return empty cleanup function if no blob created
  }, [fileBytes, mimeType, fileName]);

  const isPdf = mimeType === "application/pdf" || fileName?.toLowerCase().endsWith(".pdf");

  if (fileUrl && !fileBytes && isPdf) {
    return (
      <embed
        src={`${fileUrl}#navpanes=0&view=FitH`}
        type="application/pdf"
        width="100%"
        height="100%"
        style={{ border: "none", colorScheme: "light", ...style }}
      />
    );
  }

  // If no fileBytes provided, show message
  if (!fileBytes) {
    console.log("❌ UniversalDocumentViewer: No fileBytes provided", {
      fileBytes: fileBytes,
      mimeType: mimeType,
      fileName: fileName,
      fileUrl: fileUrl,
    });
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontFamily,
          color: "#666",
        }}
      >
        No file data available
      </div>
    );
  }

  try {
    // For PDFs, use the blob URL created in useEffect
    if (isPdf) {
      console.log("📄 Rendering PDF with blobUrl:", blobUrl);
      if (blobUrl) {
        return (
          <div
            style={{
              width: "100%",
              height: "100%",
              backgroundColor: "#ffffff",
              display: "flex",
              justifyContent: "center",
              overflow: "auto",
              colorScheme: "light",
            }}
          >
            <embed
              src={`${blobUrl}#navpanes=0&view=FitH`}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: "none", display: "block", colorScheme: "light", ...style }}
              onLoad={() => console.log("✅ PDF embed loaded successfully")}
              onError={(e) => console.error("❌ PDF embed load error:", e)}
            />
          </div>
        );
      } else {
        console.log("⏳ Waiting for PDF blob URL to be created...");
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              fontFamily,
              color: "#666",
            }}
          >
            Loading PDF...
          </div>
        );
      }
    }

    // For Excel files, parse and display as table
    if (
      mimeType?.includes("spreadsheet") ||
      fileName?.toLowerCase().endsWith(".xlsx") ||
      fileName?.toLowerCase().endsWith(".xls")
    ) {
      return <ExcelViewer fileBytes={fileBytes} />;
    }

    // For Word docs, convert to HTML
    if (
      mimeType?.includes("wordprocessing") ||
      fileName?.toLowerCase().endsWith(".docx") ||
      fileName?.toLowerCase().endsWith(".doc")
    ) {
      return <WordViewer fileBytes={fileBytes} />;
    }

    // For images, use the blob URL created in useEffect
    if (mimeType?.startsWith("image/")) {
      if (blobUrl) {
        return (
          <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
            <img
              src={blobUrl}
              alt={fileName || "Document"}
              style={{ maxWidth: "100%", maxHeight: "600px", objectFit: "contain" }}
            />
          </div>
        );
      } else {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              fontFamily,
              color: "#666",
            }}
          >
            Loading image...
          </div>
        );
      }
    }

    // For text files
    if (mimeType?.startsWith("text/") || fileName?.toLowerCase().endsWith(".txt")) {
      const text = new TextDecoder().decode(fileBytes);
      return (
        <div
          style={{
            padding: "20px",
            maxHeight: "600px",
            overflow: "auto",
            fontFamily: "monospace",
            whiteSpace: "pre-wrap",
            backgroundColor: "#f5f5f5",
          }}
        >
          {text}
        </div>
      );
    }

    console.log("❌ Unsupported file type detected:", {
      mimeType: mimeType,
      fileName: fileName,
      fileExtension: fileName?.split(".").pop()?.toLowerCase(),
      fileBytesLength: fileBytes?.byteLength || 0,
    });
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontFamily,
          color: "#666",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <div>Unsupported file type</div>
        <div style={{ fontSize: "12px" }}>
          File: {fileName || "Unknown"} | Type: {mimeType || "Unknown"}
        </div>
      </div>
    );
  } catch (err) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontFamily,
          color: "red",
        }}
      >
        Error loading document: {err.message}
      </div>
    );
  }
}

export default UniversalDocumentViewer;
